import { errorMessage } from '@/lib/api';
import { formatDate, formatLongDate, formatTime, plural } from '@/lib/format';
import { Avatar } from '@/ui/Avatar';
import { Icon } from '@/ui/Icon';
import { Callout, Skeleton } from '@/ui/bits';
import { SourceLinks } from '@/ui/Sources';
import type { HistoryRow, RecordSheet } from '@shared/record';
import { useHistory } from './queries';
import { first } from './util';

// Every change to the record: who made it, when, and the value before and after. Changes saved in one go sit together.

const DAY = /^\d{4}-\d{2}-\d{2}$/;

function show(value: string, type: HistoryRow['valueType']): string {
  return type === 'date' && DAY.test(value) ? formatLongDate(`${value}T12:00:00`) : value;
}

/** Who made the change: the teammate (who accepted it, for values from the AI), else the AI or Clientbook itself. */
function Who({ row }: { row: HistoryRow }) {
  if (!row.actor)
    return (
      <span className="hwho">
        <span className="ai-glyph sm" aria-hidden="true">
          <Icon name={row.byAi ? 'sparkle' : 'cpu'} className="i-xs" />
        </span>
        <span>{row.byAi ? 'AI' : 'Clientbook'}</span>
      </span>
    );
  return (
    <span className="hwho">
      <Avatar name={row.actor.name} hue={row.actor.hue} size="xs" />
      <span>{first(row.actor)}</span>
    </span>
  );
}

function Row({ row, lead }: { row: HistoryRow; lead: boolean }) {
  return (
    <div className="hrow">
      {lead ? <time dateTime={row.at}>{`${formatDate(row.at)} ${formatTime(row.at)}`}</time> : <span aria-hidden="true" />}
      {lead ? <Who row={row} /> : <span aria-hidden="true" />}
      <div className="hwhat">
        <b>{row.label}</b>
        {row.note ? <span className="muted"> · {row.note}</span> : null}
        {row.sources.length ? (
          <span className="muted">
            {' · from '}
            <SourceLinks sources={row.sources} />
          </span>
        ) : null}
        {row.oldValue !== null || row.newValue !== null ? (
          <div className="oldnew">
            {row.oldValue !== null ? (
              <>
                <span className="oldv">
                  <span className="rec-sr">Before: </span>
                  {show(row.oldValue, row.valueType)}
                </span>
                <Icon name="arrow-right" className="i-xs" />
              </>
            ) : null}
            {row.newValue !== null ? (
              <span className="newv">
                {row.oldValue !== null ? <span className="rec-sr">After: </span> : null}
                {show(row.newValue, row.valueType)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function History({ sheet }: { sheet: RecordSheet }) {
  const q = useHistory(sheet.type, sheet.id);
  const groups: HistoryRow[][] = [];
  for (const row of q.data?.rows ?? []) {
    const last = groups[groups.length - 1];
    if (last && last[0]!.group === row.group) last.push(row);
    else groups.push([row]);
  }
  return (
    <section className="tile" aria-labelledby={`hist-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`hist-${sheet.id}`}>Change history</h3>
        {q.data ? <span className="cnt">{plural(q.data.total, 'change')}</span> : null}
      </div>
      {q.isPending ? (
        <Skeleton rows={5} />
      ) : q.isError ? (
        <Callout
          tone="danger"
          title="The history did not load"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
              <Icon name="refresh" />
              Try again
            </button>
          }
        >
          {errorMessage(q.error)}
        </Callout>
      ) : groups.length === 0 ? (
        <p className="muted box-empty">Changes to {sheet.name} show up here, with who made them.</p>
      ) : (
        <div className="hist">
          {groups.map((g) => (
            <div key={g[0]!.id} className="hgroup">
              {g.map((row, i) => (
                <Row key={row.id} row={row} lead={i === 0} />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
